/**
 * flash.media.* — Sound, SoundChannel, SoundTransform, SoundMixer.
 *
 * All Flash-visible state (channels, position, SOUND_COMPLETE timing) runs on the runtime clock
 * (deterministic headless); audible output goes through `platform.audio` (AudioBackend).
 * Flash Player limits playback to 32 simultaneous channels: `play()` returns null beyond that.
 */
import { EventDispatcher } from '../events/EventDispatcher';
import { Event } from '../events/Event';
import { rt } from '../player/runtime';
import type { AudioBackend, AudioVoice } from '../platform/types';

export const MAX_SOUND_CHANNELS = 32;

export class SoundTransform {
  volume: number;
  pan: number;
  leftToLeft: number;
  leftToRight: number;
  rightToLeft: number;
  rightToRight: number;
  constructor(volume: number = 1, panning: number = 0) {
    this.volume = +volume;
    this.pan = +panning;
    this.leftToLeft = 1;
    this.leftToRight = 0;
    this.rightToLeft = 0;
    this.rightToRight = 1;
  }
  clone(): SoundTransform {
    return new SoundTransform(this.volume, this.pan);
  }
}

function backend(): AudioBackend | null {
  const a = rt().platform?.audio;
  return a && typeof a.play === 'function' ? (a as AudioBackend) : null;
}

export class SoundChannel extends EventDispatcher {
  /** @internal */ $sound: Sound;
  /** @internal */ $startedAt: number;
  /** @internal */ $startTime: number;
  /** @internal */ $loops: number;
  /** @internal */ $transform: SoundTransform;
  /** @internal */ $voice: AudioVoice | null;
  /** @internal */ $stopped: boolean;
  /** @internal */ $stopPosition: number;
  /** @internal time at which SOUND_COMPLETE fires */ $endTime: number;

  constructor(sound?: Sound, startTime: number = 0, loops: number = 0, transform: SoundTransform | null = null) {
    super();
    this.$sound = sound as Sound;
    this.$startedAt = rt().now();
    this.$startTime = Math.max(0, +startTime || 0);
    this.$loops = Math.max(1, loops | 0);
    this.$transform = transform ? transform.clone() : new SoundTransform();
    this.$voice = null;
    this.$stopped = !sound;
    this.$stopPosition = 0;
    const len = sound ? sound.$durationMs : 0;
    this.$endTime = this.$startedAt + Math.max(0, len - this.$startTime) * this.$loops;
  }

  get position(): number {
    if (this.$stopped) return this.$stopPosition;
    const len = this.$sound.$durationMs;
    const elapsed = rt().now() - this.$startedAt;
    const per = Math.max(0, len - this.$startTime);
    if (per <= 0) return this.$startTime;
    const played = Math.min(elapsed, per * this.$loops);
    const inLoop = played >= per * this.$loops ? per : played % per;
    return this.$startTime + inLoop;
  }
  get leftPeak(): number {
    return this.$stopped ? 0 : this.$transform.volume * SoundMixer.$transform.volume;
  }
  get rightPeak(): number {
    return this.leftPeak;
  }
  get soundTransform(): SoundTransform {
    return this.$transform.clone();
  }
  set soundTransform(t: SoundTransform) {
    this.$transform = t ? t.clone() : new SoundTransform();
    this.$applyVolume();
  }

  /** @internal */ $applyVolume(): void {
    if (this.$voice) this.$voice.setVolume(this.$transform.volume * SoundMixer.$transform.volume, this.$transform.pan);
  }

  stop(): void {
    if (this.$stopped) return;
    this.$stopPosition = this.position;
    this.$stopped = true;
    if (this.$voice) this.$voice.stop();
    rt().channels.delete(this);
  }

  /** @internal Called by the player when the channel's end time has passed. */
  $complete(): void {
    if (this.$stopped) return;
    this.$stopPosition = this.position;
    this.$stopped = true;
    if (this.$voice) this.$voice.stop();
    rt().channels.delete(this);
    this.$dispatch(new Event(Event.SOUND_COMPLETE, false, false));
  }
}

export class Sound extends EventDispatcher {
  /** @internal resolved audio URL ('' for synthetic) */ $url: string;
  /** @internal */ $durationMs: number;
  /** @internal */ $className: string;

  constructor(_stream: any = null, _context: any = null) {
    super();
    this.$url = '';
    this.$durationMs = 0;
    this.$className = '';
  }

  /** @internal Library construction. */
  static $create(url: string, durationMs: number, className = ''): Sound {
    const s = new Sound();
    s.$url = url;
    s.$durationMs = Math.max(0, +durationMs || 0);
    s.$className = className;
    return s;
  }

  get length(): number {
    return this.$durationMs;
  }
  get bytesLoaded(): number {
    return 1;
  }
  get bytesTotal(): number {
    return 1;
  }
  get isBuffering(): boolean {
    return false;
  }
  get url(): string | null {
    return this.$url || null;
  }
  get id3(): any {
    return {};
  }

  play(startTime: number = 0, loops: number = 0, sndTransform: SoundTransform | null = null): SoundChannel {
    const r = rt();
    if (r.channels.size >= MAX_SOUND_CHANNELS) return null as unknown as SoundChannel;
    const ch = new SoundChannel(this, startTime, loops, sndTransform);
    r.channels.add(ch);
    const b = backend();
    if (b) {
      try {
        ch.$voice = b.play({
          url: this.$url,
          startTime: ch.$startTime,
          loops: ch.$loops,
          volume: ch.$transform.volume * SoundMixer.$transform.volume,
          pan: ch.$transform.pan,
          durationMs: this.$durationMs,
        });
      } catch (err) {
        r.reportError(err, 'audio backend play');
      }
    }
    return ch;
  }

  close(): void {
    /* nothing streamed */
  }
  load(_req: any, _ctx: any = null): void {
    /* network loading is not part of the port */
  }
}

export class SoundMixer {
  /** @internal */ static $transform = new SoundTransform();
  static bufferTime = 1000;

  static get soundTransform(): SoundTransform {
    return SoundMixer.$transform.clone();
  }
  static set soundTransform(t: SoundTransform) {
    SoundMixer.$transform = t ? t.clone() : new SoundTransform();
    for (const ch of rt().channels) (ch as SoundChannel).$applyVolume();
  }
  static stopAll(): void {
    for (const ch of [...rt().channels]) (ch as SoundChannel).stop();
  }
  static areSoundsInaccessible(): boolean {
    return false;
  }
  static computeSpectrum(_out: any, _fft = false, _stretch = 0): void {
    /* no audio analysis */
  }
}

/** @internal Fire SOUND_COMPLETE for channels whose end time has passed (called once per tick). */
export function processSoundCompletions(now: number): void {
  const due = [...rt().channels].filter((c: any) => !c.$stopped && c.$endTime <= now) as SoundChannel[];
  due.sort((a, b) => a.$endTime - b.$endTime);
  for (const ch of due) ch.$complete();
}
